/**
 * The Reach clock gate (plan I2; spec §3.5, D-099): forwards real time to the strategic client while the game is
 * not paused. The loop's `FrameInfo.paused` covers every M1 pause reason (menu, hidden tab, blur, pointer lock,
 * gamepad); M2's docked hold (`FrameInfo.held`) is not a pause, so held frames still forward their time.
 *
 * Hidden time is measured with the Visibility API (rAF stops while hidden, so no frame time is double counted):
 * a tab hidden for at least `hiddenResumeS` (strategic.json `catchUp.hiddenResumeS`) runs the catch-up flow once
 * on its return; a shorter absence is simply lost time, like a pause.
 *
 * Besides the game pause, the gate has its own Reach pause (`setPaused`: the dev panel's "pause Reach", I8):
 * while it is on, no frame time is forwarded and a long absence does not run the catch-up, so the Reach day holds
 * still while the local game keeps flying and docking. It replaces I8's wrap of `client.tick`.
 *
 * The flight dashboard in *Pause* mode (`HOLD.dashboard`, dashboard spec §1.3, card D1) pauses the whole world, the
 * Reach day included: `frozen` reports it and those frames forward nothing. Docked held frames still forward
 * (D-099); the dashboard in *Keep running* mode adds no hold, so it forwards too.
 * `frame` is allocation-free (called every rendered frame).
 */

/** What the gate drives (the strategic client's `tick`). */
export interface ClockTarget {
  tick(realSeconds: number): void
}

/** The per-frame fields the gate reads (a `FrameInfo`). */
export interface GateFrame {
  readonly dt: number
  readonly paused: boolean
}

export interface ClockGateOptions {
  target: ClockTarget
  /** Hidden seconds from which the return runs `onLongHidden` (strategic.json `catchUp.hiddenResumeS`). */
  hiddenResumeS: number
  /** Wall clock in milliseconds (default `Date.now`). */
  now?: () => number
  /** Called once per long absence with the hidden seconds (the catch-up flow). */
  onLongHidden: (hiddenSeconds: number) => void
  /** True while a hold that pauses the world is on (the dashboard in Pause mode): no time is forwarded. */
  frozen?: () => boolean
}

export interface ClockGate {
  /** Once per rendered frame: forwards `dt` unless paused or frozen. Allocation-free. */
  frame(f: GateFrame): void
  /** The document's visibility changed (`document.hidden`). */
  visibility(hidden: boolean): void
  /** Real seconds forwarded so far (tests and the debug API). */
  readonly forwardedS: number
  /** True while the Reach pause is on (`setPaused`); the game pause is not included. */
  readonly paused: boolean
  /** Turns the Reach pause on or off: while on, the Reach clock stands still (no ticks, no catch-up). */
  setPaused(on: boolean): void
}

export function createClockGate(opts: ClockGateOptions): ClockGate {
  const now = opts.now ?? Date.now
  const frozen = opts.frozen ?? ((): boolean => false)
  let forwardedS = 0
  let hiddenAt = -1
  let paused = false
  return {
    frame(f: GateFrame): void {
      if (paused || f.paused || !(f.dt > 0) || frozen()) return
      forwardedS += f.dt
      opts.target.tick(f.dt)
    },
    visibility(hidden: boolean): void {
      if (hidden) {
        if (hiddenAt < 0) hiddenAt = now()
        return
      }
      if (hiddenAt < 0) return
      const hiddenS = (now() - hiddenAt) / 1000
      hiddenAt = -1
      if (!paused && hiddenS >= opts.hiddenResumeS) opts.onLongHidden(hiddenS)
    },
    get forwardedS(): number {
      return forwardedS
    },
    get paused(): boolean {
      return paused
    },
    setPaused(on: boolean): void {
      paused = on
    },
  }
}

/** Wires `gate.visibility` to `document.visibilitychange`; returns the unsubscribe function. */
export function watchVisibility(gate: ClockGate, doc: Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'>): () => void {
  const onChange = (): void => gate.visibility(doc.hidden)
  doc.addEventListener('visibilitychange', onChange)
  if (doc.hidden) gate.visibility(true)
  return () => doc.removeEventListener('visibilitychange', onChange)
}
